import styled from '@emotion/styled';
import { Box, Paper } from '@mui/material';
import { Link } from 'react-router-dom';

// ── Layout shell ──────────────────────────────────────────────────────────────

export const AppShell = styled(Box)`
  height: 100dvh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: var(--color-bg-body);
`;

// ── Nav ───────────────────────────────────────────────────────────────────────
//
// `Nav` is the page <header> landmark. The actual navigation list lives in
// the inner <nav> (`NavLinks`) so screen readers can distinguish "site
// header" from "primary navigation". Same convention applies to the footer.

/**
 * Height of the AppLayout navigation bar in pixels. Matches the `height`
 * declaration on `NavInner` below; exported so consumers further down the
 * tree (e.g. the editor and reader outline sidebars positioning themselves
 * with `position: fixed`) can offset by this amount without duplicating
 * the magic number or relying on a JS measurement that races with first
 * render.
 */
export const APP_NAV_HEIGHT_PX = 60;

export const Nav = styled('header')`
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(16px) saturate(160%);
  background: color-mix(in srgb, var(--color-bg-surface) 80%, transparent);
  border-bottom: 1px solid var(--color-border-default);
`;

export const NavInner = styled(Box)`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-6);
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-4);
`;

export const NavLogo = styled(Link)`
  font-family: var(--font-logo);
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text-primary);
  text-decoration: none;
  letter-spacing: -0.02em;
  transition: color var(--duration-shortest) ease;
  flex-shrink: 0;
  border-radius: var(--radius-sm);

  &:hover {
    color: var(--color-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 4px;
  }
`;

/* Desktop nav links. Visibility is driven by the `$hidden` prop — set by the
 * `useResponsiveNav` hook when the content doesn't fit in the container. When
 * hidden we keep the element mounted (and measurable) but take it out of the
 * flow via position:absolute so it doesn't contribute to the container's
 * intrinsic width. `aria-hidden` and `inert` should be applied at the call
 * site to hide it from assistive tech and the tab order. */
export const NavLinks = styled('nav')<{ $hidden?: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-2);

  ${({ $hidden }) =>
    $hidden &&
    `
    visibility: hidden;
    position: absolute;
    pointer-events: none;
    top: 0;
    left: -9999px;
  `}
`;

export const NavLink = styled(Link)`
  font-size: 14px;
  font-weight: 400;
  color: var(--color-text-secondary);
  text-decoration: none;
  padding: var(--spacing-1-5) var(--spacing-3);
  border-radius: var(--radius-md);
  white-space: nowrap;
  transition:
    color var(--duration-shortest) ease,
    background-color var(--duration-shortest) ease;

  &:hover {
    color: var(--color-text-primary);
    background: var(--color-bg-hover);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
  }
`;

export const NavLinkMuted = styled(NavLink)`
  color: var(--color-text-disabled);
`;

export const NavButton = styled(Link)`
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  padding: 7px var(--spacing-4);
  border-radius: var(--radius-md);
  background: var(--color-primary);
  color: var(--color-text-inverse);
  white-space: nowrap;
  transition:
    opacity var(--duration-shortest) ease,
    transform var(--duration-shortest) ease;

  &:hover {
    opacity: 0.88;
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
  }
`;

/**
 * Visually identical to NavButton but rendered as a <button> rather than a
 * Link. Used by entry points whose click should open a dropdown menu rather
 * than navigate immediately — e.g. "Write" needs to ask whether you want a
 * standard journal or an interactive story before routing.
 *
 * Why a separate styled component instead of `as="button"`: the styled-
 * components Link extension carries link-only props (to, replace, state, …)
 * that don't exist on <button>, so re-using the same styled() wrapper
 * surfaces TypeScript errors when callers pass button-only props like
 * `aria-haspopup`. Splitting them keeps both component contracts clean.
 */
export const NavMenuButton = styled.button`
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  text-decoration: none;
  padding: 7px var(--spacing-4);
  border-radius: var(--radius-md);
  background: var(--color-primary);
  color: var(--color-text-inverse);
  border: none;
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition:
    opacity var(--duration-shortest) ease,
    transform var(--duration-shortest) ease;

  &:hover {
    opacity: 0.88;
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
  }
`;

export const NavUserArea = styled(Box)`
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--spacing-1);
  margin-left: var(--spacing-2);
  padding-left: var(--spacing-2);
  border-left: 1px solid var(--color-border-default);
`;

/* NavAvatar is now a <button> — it toggles the dropdown menu, so a button is
 * the correct semantic element. The previous implementation used a <Link>
 * with preventDefault, which announced as a link to a profile but actually
 * acted as a button. */
export const NavAvatar = styled('button')`
  width: 32px;
  height: 32px;
  border-radius: var(--radius-full);
  overflow: hidden;
  background: var(--color-primary);
  border: 0;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-inverse);
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  flex-shrink: 0;
  cursor: pointer;
  transition: opacity var(--duration-shortest) ease;

  &:hover {
    opacity: 0.85;
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

/* Sign-out button — proper button styling rather than `all: unset` so focus
 * styles, cursor, and pressed state work without re-implementing them. */
export const NavSignOutButton = styled('button')`
  background: none;
  border: 0;
  cursor: pointer;
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 400;
  color: var(--color-text-disabled);
  padding: var(--spacing-1-5) var(--spacing-3);
  border-radius: var(--radius-md);
  transition:
    color var(--duration-shortest) ease,
    background-color var(--duration-shortest) ease;

  &:hover {
    color: var(--color-text-primary);
    background: var(--color-bg-hover);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
  }
`;

/* Desktop search input — fixed width. Previously shrank to 140px below
 * 900px as a stopgap for overflow; the useResponsiveNav hook now flips the
 * whole nav to compact mode before that's needed, so a single width is
 * simpler and avoids awkward mid-sized states. role="search" is applied at
 * the call site for the search landmark. */
export const NavSearchForm = styled('form')`
  display: flex;
  width: 200px;
`;

// ── Main content ──────────────────────────────────────────────────────────────
//
// Laid out as a flex column with two children: the OutletWrapper (page
// content) and the AppFooter (legal links). The OutletWrapper uses
// `flex: 1 0 auto` so it grows to fill any leftover height when the page is
// shorter than the viewport, which pushes the footer to the bottom of the
// scroll container. When the page is taller than the viewport the wrapper
// takes its natural height and MainContent's `overflow-y: auto` scrolls the
// whole column — footer included — as expected.

export const MainContent = styled('main')`
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

export const OutletWrapper = styled('div')`
  flex: 1 0 auto;
  width: 100%;
`;

// ── Avatar dropdown ───────────────────────────────────────────────────────────

export const AvatarDropdown = styled(Paper)`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 176px;
  background: var(--color-bg-surface) !important;
  border: 1px solid var(--color-border-default) !important;
  border-radius: var(--radius-lg) !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
  padding: var(--spacing-1-5) 0;
  z-index: 200;
  overflow: hidden;
` as typeof Paper;
(AvatarDropdown as unknown as { defaultProps?: object }).defaultProps = {
  role: 'menu',
  'aria-label': 'User menu',
};

export const AvatarDropdownItem = styled(Link)<{ as?: string }>`
  display: block;
  width: 100%;
  padding: var(--spacing-2-5) var(--spacing-4);
  font-size: 14px;
  color: var(--color-text-secondary);
  text-decoration: none;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  transition:
    background var(--duration-shortest) ease,
    color var(--duration-shortest) ease;
  box-sizing: border-box;
  font-family: var(--font-body);

  &:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: -2px;
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }
` as unknown as typeof Link & { defaultProps?: { role?: string } };
(AvatarDropdownItem as unknown as { defaultProps?: object }).defaultProps = { role: 'menuitem' };

export const AvatarDropdownDivider = styled('hr')`
  margin: var(--spacing-1) 0;
  border: none;
  border-top: 1px solid var(--color-border-default);
`;

/* Hamburger button — proper button styling instead of `all: unset` so
 * focus / hover / disabled styling works without manual re-implementation.
 * Rendered (and hidden) by the parent based on the useResponsiveNav hook. */
export const HamburgerButton = styled('button')`
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 5px;
  width: 36px;
  height: 36px;
  border-radius: var(--radius-md);
  cursor: pointer;
  flex-shrink: 0;
  border: 1px solid var(--color-border-default);
  padding: 0;

  &:hover {
    background: var(--color-bg-hover);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
  }
`;

export const HamburgerBar = styled('span')<{ $open: boolean; $i: number }>`
  display: block;
  width: 18px;
  height: 2px;
  background: var(--color-text-secondary);
  border-radius: 2px;
  transition:
    transform 240ms ease,
    opacity 240ms ease;
  ${({ $open, $i }) => $open && $i === 0 && 'transform: translateY(7px) rotate(45deg);'}
  ${({ $open, $i }) => $open && $i === 1 && 'opacity: 0; transform: scaleX(0);'}
  ${({ $open, $i }) => $open && $i === 2 && 'transform: translateY(-7px) rotate(-45deg);'}
`;

// ── Mobile drawer + overlay ───────────────────────────────────────────────────
//
// Visibility is React-driven (via `isCompact` from useResponsiveNav and
// `$open` for the drawer toggle). When closed, the parent applies the `inert`
// attribute so off-screen drawer links/buttons are removed from the tab
// order — `aria-hidden` alone does not do this.

export const MobileOverlay = styled('div')<{ $open: boolean }>`
  display: block;
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 150;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'all' : 'none')};
  transition: opacity 220ms ease;
`;

export const MobileDrawer = styled('nav')<{ $open: boolean }>`
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(300px, 86vw);
  background: var(--color-bg-surface);
  border-left: 1px solid var(--color-border-default);
  z-index: 160;
  padding: 72px var(--spacing-5) var(--spacing-8);
  overflow-y: auto;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
  transform: ${({ $open }) => ($open ? 'translateX(0)' : 'translateX(100%)')};
  transition: transform 260ms ease;
`;

export const DrawerNavLink = styled(Link)`
  display: block;
  font-size: 16px;
  font-weight: 400;
  color: var(--color-text-secondary);
  text-decoration: none;
  padding: var(--spacing-3-5) 0;
  border-bottom: 1px solid var(--color-border-default);
  transition: color var(--duration-shortest) ease;

  &:hover {
    color: var(--color-text-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
  }
`;

export const DrawerNavButton = styled(Link)`
  display: block;
  text-align: center;
  font-size: 15px;
  font-weight: 500;
  text-decoration: none;
  padding: 13px var(--spacing-5);
  border-radius: var(--radius-md);
  background: var(--color-primary);
  color: var(--color-text-inverse);
  margin-top: var(--spacing-4);
  transition: opacity 150ms ease;

  &:hover {
    opacity: 0.88;
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
  }
`;

export const DrawerDivider = styled('hr')`
  margin: var(--spacing-3) 0;
  border: none;
  border-top: 1px solid var(--color-border-default);
`;

export const DrawerSignOutButton = styled('button')`
  background: none;
  border: 0;
  display: block;
  width: 100%;
  font-size: 16px;
  font-weight: 400;
  color: var(--color-text-secondary);
  text-decoration: none;
  text-align: left;
  padding: var(--spacing-3-5) 0;
  border-bottom: 1px solid var(--color-border-default);
  cursor: pointer;
  transition: color var(--duration-shortest) ease;
  font-family: var(--font-body);

  &:hover {
    color: var(--color-text-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
  }
`;

// ── App footer ───────────────────────────────────────────────────────────────
//
// Compact legal-links strip shown inside <MainContent> so it scrolls with the
// page content. The outer element is <footer>; the inner list of legal links
// is wrapped in <nav aria-label="Legal"> so screen readers can recognize it
// as a discrete navigation region (mirroring the header pattern).

export const AppFooter = styled('footer')`
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  border-top: 1px solid var(--color-border-default);
  padding: var(--spacing-6);
  margin-top: var(--spacing-8);
  background: var(--color-bg-surface);
`;

/* AppFooterInner is the constrained-width row that lays the nav and status
 * badge out side-by-side on wide screens and stacks them (each centered) on
 * narrow ones. The nav grows with `flex: 1 1 auto` so the status badge sits
 * pinned to the right of the 1200px container; on mobile we collapse the
 * row to centered children. */
export const AppFooterInner = styled('div')`
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-3) var(--spacing-4);

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

/* The legal-links region rendered inside the AppFooter. <nav> with an
 * aria-label gives AT a navigable region named "Legal links". */
export const AppFooterNav = styled('nav')`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  max-width: 100%;
  gap: var(--spacing-3);
  font-size: 13px;
  color: var(--color-text-secondary);
  flex: 1 1 auto;
  min-width: 0;

  @media (max-width: 768px) {
    flex: 0 0 auto;
    justify-content: center;
    text-align: center;
  }
`;

export const AppFooterLink = styled(Link)`
  color: var(--color-text-secondary);
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color var(--duration-shortest) ease;

  &:hover {
    color: var(--color-text-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

/* External-link sibling of AppFooterLink for off-site URLs (e.g. the
 * BetterStack-hosted status page). Renders an <a> instead of a router
 * <Link> so it doesn't try to match against the SPA router, but shares
 * the visual treatment so it sits cleanly inside the legal nav row. */
export const AppFooterExternalLink = styled('a')`
  color: var(--color-text-secondary);
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color var(--duration-shortest) ease;

  &:hover {
    color: var(--color-text-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

/* Wrapper for the live status badge. On wide viewports it sits to the
 * right of the legal nav row inside <AppFooterInner>; below 768px the row
 * collapses and the badge takes its own line, centered. The badge itself
 * is rendered by <StatusBadge>. */
export const AppFooterStatus = styled('div')`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

/* Visual-only separator between footer links — aria-hidden so it's not
 * announced. Renders as a centered dot. */
export const AppFooterSep = styled('span')`
  opacity: 0.5;
`;

/* Cookie preferences button styled to match the link row. Proper button
 * styling instead of `all: unset` so focus/hover work natively. */
export const AppFooterCookieButton = styled('button')`
  background: none;
  border: 0;
  cursor: pointer;
  color: inherit;
  font: inherit;
  padding: 0;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: var(--color-text-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
    border-radius: 2px;
  }
`;
